import React from 'react';
import type { ModelData, ParticipantRole, ReleaseInput } from '@shared/types';

interface FinalContractProps {
  release: ReleaseInput;
  model: ModelData;
}

const ROLE_LABELS: Record<ParticipantRole, string> = {
  model: 'Subject',
  makeup_artist: 'Makeup Artist',
  stylist: 'Stylist',
  retoucher: 'Retoucher',
  assistant: 'Assistant',
  other: 'Contributor',
};

const formatDate = (dateString?: string) => {
  if (!dateString) return '__________';
  const date = new Date(dateString);
  return date
    .toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })
    .replace(/\//g, '.');
};

const FinalContract: React.FC<FinalContractProps> = ({ release, model }) => {
  const isModel = release.role === 'model' || !release.role;
  const roleLabel = ROLE_LABELS[release.role || 'model'];
  const partyDefinedTerm = isModel ? 'Subject' : 'Contributor';
  const photographerLine = release.photographer.company
    ? `${release.photographer.name}, of ${release.photographer.company}`
    : release.photographer.name;

  return (
    <div
      id="contract-preview"
      className="w-full max-w-[210mm] mx-auto bg-white p-10 md:p-12 text-sm leading-relaxed text-gray-800 border border-gray-200 shadow-xl print:border-none print:shadow-none print:w-[210mm] print:h-auto print:p-0 overflow-hidden font-sans"
      style={{ fontFamily: "'Inter', sans-serif" }}
    >
      {/* Header */}
      <div className="text-center mb-6 border-b-2 border-gray-900 pb-4">
        <h1 className="text-2xl font-bold uppercase tracking-widest serif text-black mb-2">
          {isModel ? 'Model Release Agreement' : 'Contributor Agreement'}
        </h1>
        <p className="text-gray-500 uppercase tracking-widest text-[10px] font-medium">
          {isModel
            ? 'Likeness, Publicity & Editorial Use Consent'
            : 'Work-for-Hire and Assignment of Rights'}
        </p>
      </div>

      <div className="space-y-3 text-justify text-[10.5px] md:text-[11px] leading-snug">
        {/* Preamble */}
        <p>
          I,{' '}
          <span className="font-bold border-b border-gray-400 px-1 text-black">
            {model.fullName}
          </span>{' '}
          (the &ldquo;{partyDefinedTerm}&rdquo;)
          {model.address && (
            <>
              , residing at{' '}
              <span className="italic border-b border-gray-300 px-1">{model.address}</span>
            </>
          )}
          , for good and valuable consideration — the receipt and sufficiency of which I hereby
          acknowledge, including the opportunity to participate in the Project described below —
          enter into this agreement (the &ldquo;Release&rdquo;) with{' '}
          <span className="font-bold border-b border-gray-400 px-1 text-black">
            {photographerLine}
          </span>{' '}
          (the &ldquo;Photographer&rdquo;), together with the Photographer&rsquo;s licensees,
          assigns, successors, heirs, and authorized publishers (collectively, the
          &ldquo;Authorized Parties&rdquo;).
        </p>

        {/* Shoot details */}
        <div className="bg-gray-50 p-4 border-l-4 border-gray-900 my-3">
          <h3 className="font-bold mb-2 text-[10px] uppercase tracking-widest text-gray-500">
            The Project
          </h3>
          <div className="grid grid-cols-2 gap-x-8 gap-y-1.5">
            <div className="flex flex-col">
              <span className="font-bold text-[9px] uppercase text-gray-400">Project</span>
              <span className="font-medium text-black">{release.shoot.title}</span>
            </div>
            <div className="flex flex-col">
              <span className="font-bold text-[9px] uppercase text-gray-400">Date</span>
              <span className="font-medium text-black">{formatDate(release.shoot.date)}</span>
            </div>
            <div className="col-span-2 flex flex-col mt-0.5">
              <span className="font-bold text-[9px] uppercase text-gray-400">Location</span>
              <span className="font-medium text-black">{release.shoot.location || '—'}</span>
            </div>
            {release.shoot.description && (
              <div className="col-span-2 flex flex-col mt-0.5">
                <span className="font-bold text-[9px] uppercase text-gray-400">Scope</span>
                <span className="italic text-gray-600 leading-snug">{release.shoot.description}</span>
              </div>
            )}
          </div>
        </div>

        {/* Clauses */}
        {isModel ? (
          <div className="space-y-2.5 pt-1">
            <Clause n={1} title="Grant of Rights">
              The Subject irrevocably grants the Authorized Parties the right to use, reproduce,
              edit, adapt, publish, distribute, display, perform, license, sublicense, and
              otherwise exploit any and all photographs, video, and audio recordings captured of
              the Subject in connection with the Project (the &ldquo;Photographs&rdquo;), together
              with the Subject&rsquo;s name, likeness, image, voice, and biographical information,
              in any and all media now known or hereafter devised, worldwide, in perpetuity, on a
              royalty-free basis.
            </Clause>

            <Clause n={2} title="Scope of Use">
              {release.shoot.isCommercial ? (
                <>
                  Permitted uses include, without limitation, print and digital{' '}
                  <span className="font-semibold text-black">editorial publications</span>{' '}
                  (magazines, newspapers, books, and journals), websites, social media, broadcast,
                  exhibitions, advertising, marketing and promotional materials, product packaging,
                  merchandise, stock-photography libraries, and third-party syndication. The
                  Authorized Parties may sublicense the Photographs to publishers and licensees on
                  the same terms.
                </>
              ) : (
                <>
                  Permitted uses are limited to{' '}
                  <span className="font-semibold text-black">editorial purposes</span>, including
                  print and digital magazines, newspapers, books, journalistic and editorial
                  websites, exhibitions, the Photographer&rsquo;s portfolio, and third-party
                  editorial syndication. Any commercial use — including advertising, product
                  endorsement, packaging, or merchandise — requires the Subject&rsquo;s separate
                  written consent.
                </>
              )}
            </Clause>

            <Clause n={3} title="Alterations and Context">
              The Authorized Parties may crop, retouch, color-correct, composite, caption, and
              otherwise modify the Photographs at their sole discretion, and may publish them with
              or without the Subject&rsquo;s name and in conjunction with any text, imagery, or
              editorial commentary.
            </Clause>

            <Clause n={4} title="No Approval; No Further Compensation">
              The Subject waives any right to inspect or approve the Photographs, captions,
              accompanying text, or final use prior to publication, and acknowledges no entitlement
              to any royalty, residual, or additional compensation arising from any use of the
              Photographs.
            </Clause>

            <Clause n={5} title="Release of Claims">
              The Subject releases and discharges the Authorized Parties from any and all claims,
              demands, and causes of action — including, without limitation, claims for invasion of
              privacy, violation of the right of publicity, defamation, false light, infliction of
              emotional distress, copyright infringement, and moral rights — arising out of the
              capture, reproduction, publication, or distribution of the Photographs.
            </Clause>

            <Clause n={6} title="Representations">
              The Subject represents and warrants that they are at least eighteen (18) years of
              age, of sound mind, and have full legal capacity to grant the rights set forth
              herein; and that they are not bound by any exclusive modeling, talent, or agency
              agreement that would conflict with this Release.
            </Clause>

            <Clause n={7} title="AI and Synthetic Media">
              Use of the Photographs (a) as training data for any artificial-intelligence or
              machine-learning model, or (b) to generate synthetic media depicting the
              Subject&rsquo;s likeness, is{' '}
              <span className="font-semibold text-black">excluded</span> from this Release unless
              separately agreed in writing.
            </Clause>

            <Clause n={8} title="Miscellaneous">
              This Release is binding upon the Subject&rsquo;s heirs and assigns, is governed by
              the laws of the Photographer&rsquo;s principal place of business (without regard to
              conflict-of-laws principles), and constitutes the entire agreement between the
              parties on its subject matter. If any provision is held unenforceable, the remainder
              shall remain in full force. The parties consent to execution by electronic
              signature, which shall have the same force and effect as a manuscript signature.
            </Clause>
          </div>
        ) : (
          <div className="space-y-2.5 pt-1">
            <Clause n={1} title="Work Made for Hire; Assignment">
              The Contributor acknowledges that all work, services, designs, styling, retouching,
              and other creative input contributed in connection with the Project (the
              &ldquo;Contributions&rdquo;) are &ldquo;works made for hire&rdquo; as defined in
              Section 101 of the U.S. Copyright Act of 1976, as amended, and any analogous foreign
              law. To the extent any Contributions do not so qualify, the Contributor hereby
              irrevocably assigns and transfers to the Photographer all right, title, and interest
              in and to the Contributions and the Photographs embodying them, including all
              copyright, neighboring, and related rights, worldwide and in perpetuity, on a
              royalty-free basis.
            </Clause>

            <Clause n={2} title="Scope of Rights">
              The Authorized Parties may use, reproduce, modify, publish, distribute, display,
              license, sublicense, and otherwise exploit the Contributions and the Photographs in
              any and all media now known or hereafter devised, including, without limitation,
              editorial magazines and books, advertising, marketing, stock libraries, and
              merchandise.
            </Clause>

            <Clause n={3} title="Moral Rights Waiver">
              The Contributor waives any moral rights, <em>droit moral</em>, or analogous rights
              in the Contributions and Photographs to the maximum extent permitted by applicable
              law, including any right of attribution and integrity.
            </Clause>

            <Clause n={4} title="No Credit Obligation; No Approval">
              The Authorized Parties may, but are not required to, credit the
              Contributor&rsquo;s name in connection with any use of the Photographs. The
              Contributor waives any right to inspect or approve the Photographs, captions, or
              final use prior to publication, and acknowledges no entitlement to any royalty or
              additional compensation.
            </Clause>

            <Clause n={5} title="Representations">
              The Contributor represents and warrants that they are at least eighteen (18) years
              of age, of sound mind, have full legal capacity to grant the rights set forth herein,
              that the Contributions are their original work, and that they do not infringe any
              third-party rights.
            </Clause>

            <Clause n={6} title="Miscellaneous">
              This Release is binding upon the Contributor&rsquo;s heirs and assigns, is governed
              by the laws of the Photographer&rsquo;s principal place of business (without regard
              to conflict-of-laws principles), and constitutes the entire agreement between the
              parties on its subject matter. If any provision is held unenforceable, the remainder
              shall remain in full force. The parties consent to execution by electronic
              signature.
            </Clause>
          </div>
        )}
      </div>

      {/* Signatures */}
      <div className="mt-8 pt-5 border-t border-gray-200 grid grid-cols-2 gap-10 page-break-inside-avoid">
        <div>
          <p className="font-bold mb-3 uppercase text-[10px] tracking-widest text-gray-400">
            {roleLabel}
          </p>
          <div className="h-16 mb-2 flex items-end border-b border-gray-300 pb-1">
            {model.signatureDataUrl ? (
              <img
                src={model.signatureDataUrl}
                alt="Signature"
                className="h-full object-contain object-left"
              />
            ) : (
              <div className="w-full"></div>
            )}
          </div>
          <div className="pt-1">
            <p className="font-bold text-sm text-black">{model.fullName}</p>
            <p className="text-[11px] text-gray-500 mt-1">Date: {formatDate(model.signedAt)}</p>
          </div>
        </div>

        <div>
          <p className="font-bold mb-3 uppercase text-[10px] tracking-widest text-gray-400">
            Photographer
          </p>
          <div className="h-16 mb-2 flex items-end border-b border-gray-300 pb-1">
            <span className="text-3xl signature-font text-gray-900 transform -rotate-2 origin-left block pl-2">
              {release.photographer.name}
            </span>
          </div>
          <div className="pt-1">
            <p className="font-bold text-sm text-black">{release.photographer.name}</p>
            <p className="text-[11px] text-gray-500 mt-1">
              Date: {formatDate(release.shoot.date)}
            </p>
          </div>
        </div>
      </div>

      <div className="mt-6 text-center print-only">
        <p className="text-[9px] text-gray-300 tracking-widest uppercase">
          ReleaseSnap — Executed Electronically
        </p>
      </div>
    </div>
  );
};

const Clause: React.FC<{ n: number; title: string; children: React.ReactNode }> = ({
  n,
  title,
  children,
}) => (
  <div>
    <span className="font-bold text-black mr-1.5">
      {n}. {title}.
    </span>
    <span className="text-gray-700">{children}</span>
  </div>
);

export default FinalContract;
